import { DatePipe, NgClass } from "@angular/common";
import { Component, computed, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { IApiResponse } from "@models/api-response.model";
import {
  ICategoryListEntity,
  IProductListApiResponse,
  IProductResponseDto,
} from "@models/product-list.model";
import { AppStateService, RequestService } from "@services/index";
import { ProductService } from "@services/product/product.service";

@Component({
  selector: "fh-product-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.css"],
  standalone: true,
  imports: [
    DatePipe,
    NgClass,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    FilterBarComponent,
    DataTableComponent,
    FormAutocompleteComponent,
    RefreshButtonComponent,
  ],
})
export class ProductListPageComponent implements OnInit {
  // The old x-access-token/localStorage login model wrote this to localStorage on login;
  // now that auth state comes from the session cookie, read it from AppStateService
  // instead (populated by AuthGaurd's live session check before this route activates).
  user_id = this.appstate.currentUserId();

  // Template ref for expiry column
  @ViewChild("expiryTemplate", { static: true }) expiryTemplateRef!: TemplateRef<any>;

  // State signals
  iso2 = signal<string | null>(null);
  categoryId = signal<string | null>(null);
  searchText = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("id");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);

  // Data signals
  products = signal<IProductResponseDto[]>([]);
  total = signal<number>(0);

  // Category list signals
  categoriesList = signal<ICategoryListEntity[]>([]);
  totalProductCount = signal<number>(0);

  // Status messages
  success_msg = signal<string>("");
  error_msg = signal<string>("");

  // Form controls for filter dropdowns
  countryFilterFormControl = new FormControl<string | null>(null);
  categoryFilterFormControl = new FormControl<string | null>(null);

  // Computed options for dropdowns
  destinationCountries = computed(() => this.appstate.destinationCountries());

  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.destinationCountries().map((c) => ({
      value: c.iso2,
      label: c.name,
    }));
  });

  categoryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return [
      { label: "All Categories", value: "all", count: this.totalProductCount() },
      { label: "Expired", value: "expired" },
      ...this.categoriesList().map((c) => ({
        label: c.category_name,
        value: String(c.id),
        count: Number(c.quantity) || 0,
      })),
    ];
  });

  readonly paginatorConfig = paginatorConfig;

  // Table configuration
  tableConfig = computed<TableConfig<IProductResponseDto>>(() => ({
    columns: this.getTableColumns(),
    data: this.products(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: paginatorConfig.showFirstLastButtons,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/product_input/:id",
    getDetailsRouteParams: (row) => ({ id: row.id }),
  }));

  tableActions = computed<TableAction<IProductResponseDto>[]>(() => [
    {
      icon: "content_copy",
      label: "Duplicate Product",
      handler: (row) => this.duplicateProduct(row.id),
    },
    {
      icon: "delete",
      label: "Delete Product",
      handler: (row) => this.deleteProduct(row.id),
      color: "warn",
    },
  ]);

  constructor(
    private auth: RequestService,
    private appstate: AppStateService,
    private productService: ProductService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Handle navigation state messages (e.g. from product edit page)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state as { success_msg?: string; error_msg?: string };
    if (state?.success_msg) {
      this.success_msg.set(state.success_msg);
      setTimeout(() => this.success_msg.set(""), 3000);
    } else if (state?.error_msg) {
      this.error_msg.set(state.error_msg);
      setTimeout(() => this.error_msg.set(""), 3000);
    }
  }

  ngOnInit(): void {
    this.fetchItemCountForCategories();

    this.route.queryParams.subscribe((params) => {
      this.pageIndex.set(+(params.pageIndex || 0));
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.iso2.set(params.iso2 || null);
      this.categoryId.set(params.category || null);
      this.searchText.set(params.search || "");
      this.sortProp.set(params.sortProp || "id");
      this.sortDir.set((params.sortDir || "desc") as "asc" | "desc");

      // Sync form controls with URL params
      this.countryFilterFormControl.patchValue(this.iso2(), { emitEvent: false });
      this.categoryFilterFormControl.patchValue(this.categoryId(), { emitEvent: false });

      this.loadProducts();
    });
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, pageIndex: 0 });
  }

  onCategoryChange(categoryId: string | null): void {
    this.updateQueryParams({ category: categoryId || undefined, pageIndex: 0 });
  }

  onSearchChange(value: string): void {
    this.searchText.set(value);
    this.updateQueryParams({ search: value || undefined, pageIndex: 0 });
  }

  onResetFilters(): void {
    this.countryFilterFormControl.setValue(null);
    this.categoryFilterFormControl.setValue(null);
    this.searchText.set("");
    this.updateQueryParams({
      iso2: undefined,
      category: undefined,
      search: undefined,
      pageIndex: 0,
      sortProp: undefined,
      sortDir: undefined,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
      pageIndex: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  isProductExpired(expiryDate: string | number | Date): boolean {
    return new Date(expiryDate) < new Date();
  }

  resetProductExpiry(product: IProductResponseDto): void {
    if (!confirm("This action will restart the expiry timer")) return;

    const newExpiry = this.productService.resetProductExpiry({
      expiresAt: product.expires_at!,
      expiryUpdatedAt: product.expiry_updated_at!,
    });

    this.auth
      .put(
        `/api/v1/product-expiry/reset?productId=${product.id}&newExpiry=${newExpiry.toISOString()}`
      )
      .subscribe({
        next: (res: any) => {
          if (res.result_code) {
            this.success_msg.set(res.message);
            this.products.update((list) =>
              list.map((p) =>
                p.id === product.id
                  ? { ...p, expires_at: newExpiry.toISOString(), status: "active" }
                  : p
              )
            );
          } else {
            this.error_msg.set(res.message);
          }
          setTimeout(() => {
            this.success_msg.set("");
            this.error_msg.set("");
          }, 4000);
        },
      });
  }

  duplicateProduct(id: number): void {
    this.auth.post<IApiResponse>(`/api/v2/product/${id}/duplicate`, {}).subscribe({
      next: (res) => {
        if (res.status) {
          this.success_msg.set("Product duplicated successfully");
          this.loadProducts();
        } else {
          this.error_msg.set(res.message || "Could not duplicate product");
        }
        setTimeout(() => {
          this.success_msg.set("");
          this.error_msg.set("");
        }, 4000);
      },
      error: () => {
        this.error_msg.set("Could not duplicate product. Please try again.");
        setTimeout(() => this.error_msg.set(""), 4000);
      },
    });
  }

  deleteProduct(id: number): void {
    if (!confirm("Are you sure?")) return;
    const user_id = this.appstate.currentUserId();
    this.auth
      .delete(`/api/v1/vendor/vendor_product_list?vendor_id=${user_id}&prod_id=${id}`)
      .subscribe((res: any) => {
        if (res.result_code) {
          this.products.update((list) => list.filter((p) => p.id !== id));
          this.total.update((t) => t - 1);
          this.success_msg.set("Deleted successfully");
          setTimeout(() => this.success_msg.set(""), 4000);
        }
      });
  }

  getCountryName(iso2: string): string {
    const country = this.appstate.destinationCountries().find((c) => c.iso2 === iso2);
    return country?.name || iso2;
  }

  // Bound wrapper so fh-refresh-button gets a stable, correctly-`this`-bound reference.
  refreshProducts = () => this.loadProducts();

  private fetchItemCountForCategories(): void {
    this.auth.get(`/api/v1/vendor/vendor_category_list?id=${this.user_id}`).subscribe({
      next: (res: any) => {
        this.totalProductCount.set(res.data.count || 0);
        this.categoriesList.set(
          res.data.list?.map((v: any) => ({
            id: v.id,
            category_name: v.category_name,
            quantity: v.quantity,
          })) || []
        );
      },
    });
  }

  private loadProducts(): void {
    const catId = this.categoryId();
    const isExpired = catId === "expired";
    const data = {
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      orderField: isExpired ? "expires_at" : this.sortProp(),
      order: isExpired ? "asc" : this.sortDir(),
      search: this.searchText(),
      category: catId === "all" || catId === "expired" ? "" : catId || "",
      expired: isExpired || undefined,
      iso2: this.iso2() || "",
    };

    this.isLoading.set(true);
    this.auth.post<IProductListApiResponse>("/api/v1/product_list", data).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.products.set(
            (res.data.list || [])
              .filter((i: any) => !!i)
              .map((d: any) => ({ ...d, status: d.status?.trim() }))
          );
          this.total.set(res.data.count || 0);
        } else {
          this.products.set([]);
          this.total.set(0);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.products.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<IProductResponseDto>[] {
    return [
      {
        key: "id",
        header: "ID",
        sortable: true,
        width: "80px",
      },
      {
        key: "product_image",
        header: "Image",
        sortable: false,
        type: "image",
        width: "80px",
      },
      {
        key: "product_name",
        header: "Name",
        sortable: true,
        width: "200px",
      },
      {
        key: "display_price",
        header: "Price",
        sortable: true,
        type: "number",
        width: "100px",
        format: (v) => (v != null ? Number(v).toFixed(2) : "—"),
      },
      {
        key: "qty",
        header: "Quantity",
        sortable: true,
        width: "100px",
        format: (v) => (v != null ? String(v) : "—"),
      },
      {
        key: "delivery_standard_charges",
        header: "Delivery Charges",
        sortable: false,
        width: "150px",
        format: (v) => (v != null ? String(v) : "—"),
      },
      {
        key: "subcategory_name",
        header: "Subcategory",
        sortable: false,
        format: (v) => (v != null ? String(v) : "—"),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: "100px",
      },
      {
        key: "iso2",
        header: "Country",
        sortable: false,
        width: "100px",
        format: (v) => this.getCountryName(v),
      },
      {
        key: "expires_at",
        header: "Expiry",
        sortable: false,
        width: "220px",
        template: this.expiryTemplateRef,
      },
    ];
  }
}
